import type { StaticEntityType } from "@config";
import { useIsMobile } from "@library/shared";
import { throttle } from "@library/shared/utils/throttle";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ImageShapeEnum } from "../../image";
import { TICKER_PADDING_Y } from "../constants";
import { type ITickerEditorData, TickerAnimDirectionEnum } from "../types";
import { ensureTickerElementSettings } from "../utils";

interface IProps {
  entity: StaticEntityType;
  editorData?: ITickerEditorData;
}

export const useTickerDetails = ({ entity, editorData }: IProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();
  const [maxHeight, setMaxHeight] = useState(-1);
  const [canAnimate, setCanAnimate] = useState(false);

  const elementSettings = useMemo(() => {
    return ensureTickerElementSettings(entity.settings);
  }, [entity.settings]);

  const aspectRatio = useMemo(() => {
    if (elementSettings.shape === ImageShapeEnum.Rectangle) {
      return 16 / 9;
    }
    return 1;
  }, [elementSettings.shape]);

  const isHorizontal = [
    TickerAnimDirectionEnum.Left,
    TickerAnimDirectionEnum.Right,
  ].includes(elementSettings.tickerAnimDirection);
  const isVertical = !isHorizontal;

  const isAnimReverse = [
    TickerAnimDirectionEnum.Down,
    TickerAnimDirectionEnum.Right,
  ].includes(elementSettings.tickerAnimDirection);

  useLayoutEffect(() => {
    const items = itemsRef.current;
    const wrapper = wrapperRef.current;
    if (!items || !wrapper) {
      return;
    }
    const handleResize = throttle(() => {
      if (isHorizontal) {
        setCanAnimate(items.clientWidth > wrapper.clientWidth);
        setMaxHeight(-1);
      } else if (isVertical) {
        setCanAnimate(
          items.clientHeight - TICKER_PADDING_Y * 2 > wrapper.clientHeight,
        );
        const maxHeight = wrapper.clientWidth / aspectRatio;
        setMaxHeight(maxHeight);
      }
    }, 250);

    const resizeObserver = new ResizeObserver(handleResize);

    resizeObserver.observe(wrapper);
    handleResize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    elementSettings.tickerItems.length,
    elementSettings.shape,
    elementSettings.tickerItemsSpacing,
    elementSettings.borderWidth,
    elementSettings.borderColor,
    aspectRatio,
    isHorizontal,
    isVertical,
  ]);

  let width = `${editorData?.width || elementSettings.width}%`;
  let height = editorData?.height || elementSettings.height;
  if (isMobile) {
    width = "100%";
    height = isVertical ? 320 : 128;
  }

  if (maxHeight > 0 && height > maxHeight) {
    height = maxHeight;
  }

  return {
    elementSettings,
    aspectRatio,

    isHorizontal,
    isVertical,
    isAnimReverse,

    width,
    height,
    maxHeight,
    shouldAnimate: canAnimate,

    wrapperRef,
    itemsRef,

    isEmpty: elementSettings.tickerItems.length === 0,
  };
};
